import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { 
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Typography, 
  CircularProgress, Chip, Button, Tooltip,
  useMediaQuery, Box, Avatar, Paper,
  Fade, Menu, MenuItem, ListItemIcon, ListItemText, Divider
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import MemoryIcon from "@material-ui/icons/Memory";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import BarChartIcon from "@material-ui/icons/BarChart";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import ScheduleIcon from "@material-ui/icons/Schedule";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PlaylistPlayIcon from "@material-ui/icons/PlaylistPlay";
import AddIcon from "@material-ui/icons/Add";
import StarIcon from "@material-ui/icons/Star";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Markdown from "markdown-to-jsx";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  desktopPaper: {
    position: "fixed",
    top: 72,
    right: theme.spacing(3),
    margin: 0,
    width: 480,
    height: 650,
    maxHeight: "calc(100vh - 96px)",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark" 
      ? "0 16px 48px rgba(0,0,0,0.7)" 
      : "0 16px 48px rgba(0,0,0,0.14)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    display: "flex",
    flexDirection: "column",
    zIndex: 1400,
  },
  mobilePaper: {
    margin: 0,
    width: "100%",
    height: "100dvh",
    maxHeight: "100dvh",
    borderRadius: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    zIndex: 1500,
  },
  titleBar: {
    backgroundColor: theme.palette.type === "dark" 
      ? "#1a1f26" 
      : theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  jarvisAvatar: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(33, 150, 243, 0.2)" : "rgba(255,255,255,0.2)",
    color: theme.palette.type === "dark" ? "#64b5f6" : "#ffffff",
    width: 36,
    height: 36,
    border: "1px solid rgba(255,255,255,0.25)",
  },
  titleText: {
    fontWeight: 700,
    fontSize: "0.95rem",
    lineHeight: 1.2,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#4caf50",
    display: "inline-block",
    boxShadow: "0 0 6px #4caf50",
    animation: "$pulseDot 2s infinite",
  },
  "@keyframes pulseDot": {
    "0%": { opacity: 0.6, transform: "scale(0.9)" },
    "50%": { opacity: 1, transform: "scale(1.15)" },
    "100%": { opacity: 0.6, transform: "scale(0.9)" },
  },
  subtitleText: {
    opacity: 0.85,
    fontSize: "0.72rem",
    fontWeight: 400,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  /* ── Wrapper de Navegación de Chips con Flechas y Scroll ── */
  quickChipsWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(255,255,255,0.02)" 
      : "rgba(0,0,0,0.02)",
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "relative",
    flexShrink: 0,
  },
  navArrowBtn: {
    padding: 4,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#f0f2f5",
    },
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  quickChipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    overflowX: "auto",
    padding: "2px 6px",
    scrollBehavior: "smooth",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    flex: 1,
  },
  menuTriggerChip: {
    fontSize: "0.76rem",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    height: 28,
    borderRadius: 14,
    border: `1.5px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(33, 150, 243, 0.15)" : "#e3f2fd",
    color: theme.palette.type === "dark" ? "#90caf9" : "#1565c0",
    transition: "all 0.15s ease-in-out",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(33, 150, 243, 0.25)" : "#bbdefb",
    },
    "& .MuiChip-icon": {
      marginLeft: 6,
    },
  },
  addPromptChip: {
    fontSize: "0.76rem",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    height: 28,
    borderRadius: 14,
    border: "1px dashed #10B981",
    backgroundColor: theme.palette.type === "dark" ? "rgba(16, 185, 129, 0.12)" : "#ECFDF5",
    color: "#059669",
    transition: "all 0.15s ease-in-out",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(16, 185, 129, 0.22)" : "#D1FAE5",
    },
    "& .MuiChip-icon": {
      marginLeft: 6,
    },
  },
  quickChip: {
    fontSize: "0.76rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    height: 28,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    transition: "all 0.15s ease-in-out",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(33, 150, 243, 0.15)" : "#e3f2fd",
      borderColor: theme.palette.primary.main,
    },
    "& .MuiChip-icon": {
      color: theme.palette.type === "dark" ? "#90caf9" : theme.palette.primary.main,
      marginLeft: 6,
    },
  },
  customQuickChip: {
    fontSize: "0.76rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    height: 28,
    borderRadius: 14,
    border: "1px solid #F59E0B",
    backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.12)" : "#FFFBEB",
    color: theme.palette.type === "dark" ? "#FBBF24" : "#B45309",
    transition: "all 0.15s ease-in-out",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.22)" : "#FEF3C7",
    },
    "& .MuiChip-icon": {
      marginLeft: 6,
    },
    "& .MuiChip-deleteIcon": {
      color: "#F59E0B",
      "&:hover": {
        color: "#DC2626",
      },
    },
  },

  chatArea: {
    flex: 1,
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    overflowY: "auto",
    backgroundColor: theme.palette.type === "dark" 
      ? "#12161c" 
      : "#f4f6f9",
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  myMessage: {
    alignSelf: "flex-end",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "10px 14px 6px 14px",
    borderRadius: "14px 14px 3px 14px",
    maxWidth: "85%",
    wordBreak: "break-word",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    display: "flex",
    flexDirection: "column",
  },
  myMessageText: {
    fontSize: "0.88rem",
    lineHeight: 1.45,
    color: "#fff",
  },
  myMessageTime: {
    fontSize: "0.65rem",
    opacity: 0.8,
    alignSelf: "flex-end",
    marginTop: 4,
  },
  jarvisMessage: {
    alignSelf: "flex-start",
    backgroundColor: theme.palette.type === "dark" ? "#1e242c" : "#ffffff",
    color: theme.palette.text.primary,
    padding: "12px 14px 6px 14px",
    borderRadius: "14px 14px 14px 3px",
    maxWidth: "92%",
    wordBreak: "break-word",
    boxShadow: theme.palette.type === "dark"
      ? "0 2px 6px rgba(0,0,0,0.25)"
      : "0 1px 5px rgba(0,0,0,0.06)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
    display: "flex",
    flexDirection: "column",
  },
  jarvisMessageTime: {
    fontSize: "0.65rem",
    opacity: 0.6,
    alignSelf: "flex-end",
    marginTop: 4,
  },
  markdownContent: {
    fontSize: "0.87rem",
    lineHeight: 1.5,
    "& p": {
      margin: "0 0 6px 0",
      "&:last-child": { marginBottom: 0 },
    },
    "& strong": {
      fontWeight: 700,
      color: theme.palette.type === "dark" ? "#90caf9" : "#0d47a1",
    },
    "& ul, & ol": {
      margin: "4px 0 6px 0",
      paddingLeft: 20,
    },
    "& li": {
      marginBottom: 3,
    },
    "& code": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
      padding: "2px 4px",
      borderRadius: 4,
      fontSize: "0.82rem",
      fontFamily: "monospace",
    },
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      margin: "8px 0",
      fontSize: "0.82rem",
    },
    "& th, & td": {
      border: `1px solid ${theme.palette.divider}`,
      padding: "6px 8px",
      textAlign: "left",
    },
    "& th": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      fontWeight: 700,
    },
  },
  actionCardContainer: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(33, 150, 243, 0.08)" 
      : "#f0f7ff",
    border: `1px dashed ${theme.palette.type === "dark" ? "#2196f3" : "#90caf9"}`,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  actionCardTitle: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#90caf9" : "#1565c0",
  },
  actionCardButtons: {
    display: "flex",
    gap: 8,
  },
  confirmBtn: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.76rem",
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1b5e20",
    },
  },
  cancelBtn: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.76rem",
  },
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 14,
    backgroundColor: theme.palette.type === "dark" ? "#1e242c" : "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    alignSelf: "flex-start",
    border: `1px solid ${theme.palette.divider}`,
  },
  inputArea: {
    padding: "8px 12px 10px 12px",
    backgroundColor: theme.palette.type === "dark" ? "#1a1f26" : "#ffffff",
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  inputCapsule: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f0f2f5",
    borderRadius: 24,
    padding: "2px 6px 2px 10px",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
  },
  textFieldSeamless: {
    flex: 1,
    "& .MuiInputBase-root": {
      fontSize: "0.88rem",
      padding: "6px 8px",
    },
    "& .MuiInput-underline:before": { display: "none" },
    "& .MuiInput-underline:after": { display: "none" },
  },
  micActive: {
    color: "#f44336 !important",
    animation: "$pulseMic 1.2s infinite",
  },
  "@keyframes pulseMic": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.2)" },
    "100%": { transform: "scale(1)" },
  },
}));

// Quick chips del sistema (profesionales, sin emojis)
const QUICK_ACTIONS = [
  { label: "Métricas del Día", prompt: "Dame el reporte de métricas del día de hoy con tickets abiertos, cerrados y tiempos.", icon: <BarChartIcon style={{ fontSize: 16 }} /> },
  { label: "Estado del Kanban", prompt: "¿Cuáles son las etapas del Kanban y cuántos tickets activos hay en cada etapa?", icon: <TrendingUpIcon style={{ fontSize: 16 }} /> },
  { label: "Carga de Asesores", prompt: "¿Cómo está distribuida la carga de trabajo entre los asesores del CRM?", icon: <PeopleOutlineIcon style={{ fontSize: 16 }} /> },
  { label: "Leads Inactivos", prompt: "Busca y muéstrame los leads sin interacción por más de 7 días para limpiar o reactivar.", icon: <ScheduleIcon style={{ fontSize: 16 }} /> },
  { label: "Base de Conocimiento", prompt: "¿Cuáles son los productos, precios y promociones registrados en la base de conocimiento?", icon: <MenuBookIcon style={{ fontSize: 16 }} /> },
];

const INITIAL_MESSAGE = {
  text: "Hola admin, soy Jarvis. Estoy listo para ayudarte a auditar prospectos, consultar el estado del Kanban, revisar métricas de venta y asistirte en la gestión operativa de OpenSales. ¿Qué deseas consultar?",
  fromMe: false,
  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
};

const JarvisChatModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);

  // Estados para Tareas Frecuentes / Memoria
  const [customActions, setCustomActions] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskPrompt, setNewTaskPrompt] = useState("");

  const chatEndRef = useRef(null);
  const chipsBarRef = useRef(null);
  const recognitionRef = useRef(null);

  const storageKey = `jarvis_custom_tasks_${user?.companyId || 1}`;

  // Cargar Tareas Personalizadas (localStorage + backend Settings)
  useEffect(() => {
    if (!user || user.profile !== "admin") return;

    // 1. Carga instantánea de localStorage
    const cached = localStorage.getItem(storageKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setCustomActions(parsed);
        }
      } catch (e) {
        console.warn("Error parsing cached tasks", e);
      }
    }

    // 2. Sincronización con backend (Settings)
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings/jarvisCustomActions");
        if (data && data.value) {
          const parsed = JSON.parse(data.value);
          if (Array.isArray(parsed)) {
            setCustomActions(parsed);
            localStorage.setItem(storageKey, JSON.stringify(parsed));
          }
        }
      } catch (err) {
        // Ignorar si aún no existe la configuración
      }
    };

    fetchSettings();
  }, [user, storageKey]);

  // Persistir cambios en Tareas Personalizadas
  const persistCustomActions = async (updated) => {
    setCustomActions(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
    try {
      await api.put("/settings/jarvisCustomActions", { value: JSON.stringify(updated) });
    } catch (err) {
      console.warn("Could not save to backend settings:", err);
    }
  };

  const handleSaveCustomAction = () => {
    if (!newTaskTitle.trim() || !newTaskPrompt.trim()) return;

    const newItem = {
      id: `task_${Date.now()}`,
      label: newTaskTitle.trim(),
      prompt: newTaskPrompt.trim(),
      isCustom: true,
    };

    const updated = [...customActions, newItem];
    persistCustomActions(updated);
    setNewTaskTitle("");
    setNewTaskPrompt("");
    setCreateModalOpen(false);
    toast.success("Tarea guardada en la memoria de Jarvis");
  };

  const handleDeleteCustomAction = (id) => {
    const updated = customActions.filter((a) => a.id !== id);
    persistCustomActions(updated);
    toast.info("Tarea eliminada de la memoria");
  };

  // Scroll horizontal en la barra con rueda del mouse
  const handleChipsWheel = (e) => {
    if (chipsBarRef.current) {
      e.preventDefault();
      chipsBarRef.current.scrollLeft += e.deltaY * 0.9;
    }
  };

  // Navegación con flechas en desktop
  const scrollChips = (offset) => {
    if (chipsBarRef.current) {
      chipsBarRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  // Menú desplegable
  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = "es-PE";

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((r) => r[0].transcript)
          .join("");
        setInput(transcript);
      };

      recognition.onerror = (e) => {
        console.warn("Speech recognition error:", e);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }
  }, []);

  useEffect(() => {
    if (open) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.info("El dictado por voz no está soportado en este navegador.");
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleOpenTicket = (ticketId) => {
    if (!ticketId) return;
    history.push(`/tickets/${ticketId}`);
    if (isMobile) {
      onClose();
    }
    toast.info(`Abriendo ticket #${ticketId}`);
  };

  const handleSend = async (manualText) => {
    const textToSend = typeof manualText === "string" ? manualText : input;
    if (!textToSend || !textToSend.trim()) return;

    const trimmed = textToSend.trim();
    const currentTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    setMessages((prev) => [
      ...prev,
      { text: trimmed, fromMe: true, time: currentTime },
    ]);
    setInput("");
    setLoading(true);

    try {
      const response = await api.post("/jarvis/chat", { command: trimmed });
      const replyText = response.data?.response || "Comando procesado correctamente.";
      const replyTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      setMessages((prev) => [
        ...prev,
        { text: replyText, fromMe: false, time: replyTime },
      ]);
    } catch (error) {
      console.error(error);
      const errorTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      setMessages((prev) => [
        ...prev,
        { text: "Error de conexión con Jarvis. Verifica tu conexión o intenta nuevamente.", fromMe: false, time: errorTime },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([INITIAL_MESSAGE]);
    toast.info("Historial del chat reiniciado.");
  };

  const formatMarkdownText = (rawText) => {
    if (!rawText) return "";
    return rawText.replace(/#(\d{4,8})\b/g, "[#$1](#ticket-$1)");
  };

  const markdownOptions = useMemo(
    () => ({
      disableParsingRawHTML: true,
      overrides: {
        a: {
          component: ({ href, children }) => {
            if (href && href.startsWith("#ticket-")) {
              const ticketId = href.replace("#ticket-", "");
              return (
                <Chip
                  size="small"
                  label={`#${ticketId}`}
                  icon={<OpenInNewIcon style={{ fontSize: 13 }} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenTicket(ticketId);
                  }}
                  clickable
                  style={{
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    height: 20,
                    margin: "0 2px",
                    backgroundColor: theme.palette.type === "dark" ? "rgba(33, 150, 243, 0.2)" : "#e3f2fd",
                    color: theme.palette.type === "dark" ? "#90caf9" : "#1976d2",
                    verticalAlign: "middle",
                  }}
                />
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main }}>
                {children}
              </a>
            );
          },
        },
      },
    }),
    [theme]
  );

  if (!user || user.profile !== "admin") {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          // No cerrar si el evento proviene de un click fuera o backdrop
          if (reason === "backdropClick") {
            return;
          }
          onClose();
        }}
        fullScreen={isMobile}
        hideBackdrop={!isMobile}
        disableBackdropClick={true}
        disableEnforceFocus={true}
        PaperProps={{
          className: isMobile ? classes.mobilePaper : classes.desktopPaper,
        }}
        aria-labelledby="jarvis-dialog-title"
      >
        {/* ── Encabezado Ejecutivo Limpio ──────────────── */}
        <DialogTitle id="jarvis-dialog-title" className={classes.titleBar} disableTypography>
          <div className={classes.headerLeft}>
            <Avatar className={classes.jarvisAvatar}>
              <MemoryIcon fontSize="small" />
            </Avatar>
            <div>
              <div className={classes.titleText}>
                Jarvis Chat IA
                <span className={classes.liveDot} title="En Línea" />
              </div>
              <Typography variant="caption" className={classes.subtitleText}>
                Asistente Ejecutivo OpenSales
              </Typography>
            </div>
          </div>

          <div className={classes.headerActions}>
            <Tooltip title="Reiniciar chat">
              <IconButton size="small" style={{ color: "#fff" }} onClick={handleClearChat}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cerrar">
              <IconButton size="small" style={{ color: "#fff" }} onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </DialogTitle>

        {/* ── Barra de Navegación de Chips con Flechas y Desplazamiento ── */}
        <div className={classes.quickChipsWrapper}>
          {/* Flecha Izquierda (Desktop) */}
          <IconButton 
            size="small" 
            className={classes.navArrowBtn} 
            onClick={() => scrollChips(-180)}
            title="Desplazar a la izquierda"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          {/* Carrusel de Chips interactivo con rueda del ratón */}
          <div 
            ref={chipsBarRef} 
            className={classes.quickChipsBar}
            onWheel={handleChipsWheel}
          >
            {/* Menú Desplegable 'Comandos ▾' */}
            <Chip
              icon={<PlaylistPlayIcon style={{ fontSize: 16 }} />}
              label="Comandos ▾"
              size="small"
              className={classes.menuTriggerChip}
              onClick={handleOpenMenu}
            />

            {/* Botón '+ Nueva Tarea' */}
            <Chip
              icon={<AddIcon style={{ fontSize: 15 }} />}
              label="+ Nueva Tarea"
              size="small"
              className={classes.addPromptChip}
              onClick={() => setCreateModalOpen(true)}
            />

            {/* Tareas Personalizadas del Usuario (Memoria) */}
            {customActions.map((action) => (
              <Chip
                key={action.id}
                icon={<StarIcon style={{ fontSize: 15, color: "#F59E0B" }} />}
                label={action.label}
                size="small"
                className={classes.customQuickChip}
                onClick={() => handleSend(action.prompt)}
                onDelete={(e) => {
                  e.stopPropagation();
                  handleDeleteCustomAction(action.id);
                }}
                disabled={loading}
              />
            ))}

            {/* Chips Predeterminados del Sistema */}
            {QUICK_ACTIONS.map((action, idx) => (
              <Chip
                key={`sys-${idx}`}
                icon={action.icon}
                label={action.label}
                size="small"
                className={classes.quickChip}
                onClick={() => handleSend(action.prompt)}
                disabled={loading}
              />
            ))}
          </div>

          {/* Flecha Derecha (Desktop) */}
          <IconButton 
            size="small" 
            className={classes.navArrowBtn} 
            onClick={() => scrollChips(180)}
            title="Desplazar a la derecha"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>

        {/* ── Área de Mensajes ───────────────── */}
        <DialogContent className={classes.chatArea}>
          {messages.map((msg, idx) => {
            const isConfirmationNeeded =
              !msg.fromMe &&
              (msg.text.includes("Escribe CONFIRMO") ||
                msg.text.includes("CONFIRMO para ejecutar") ||
                msg.text.includes("¿Confirmas la ejecución?") ||
                msg.text.includes("Responde SI o NO"));

            return (
              <div key={idx} className={classes.messageRow}>
                <div className={msg.fromMe ? classes.myMessage : classes.jarvisMessage}>
                  {/* Contenido */}
                  <div className={classes.markdownContent}>
                    {msg.fromMe ? (
                      <Typography variant="body2" className={classes.myMessageText} style={{ whiteSpace: "pre-wrap" }}>
                        {msg.text}
                      </Typography>
                    ) : (
                      <Markdown options={markdownOptions}>
                        {formatMarkdownText(msg.text)}
                      </Markdown>
                    )}
                  </div>

                  {/* Hora sutil integrada abajo a la derecha */}
                  <span className={msg.fromMe ? classes.myMessageTime : classes.jarvisMessageTime}>
                    {msg.time}
                  </span>

                  {/* Tarjeta de Acción 1-Click para Confirmaciones */}
                  {isConfirmationNeeded && (
                    <div className={classes.actionCardContainer}>
                      <div className={classes.actionCardTitle}>
                        Acción operativa detectada — Confirma con 1 clic:
                      </div>
                      <div className={classes.actionCardButtons}>
                        <Button
                          size="small"
                          variant="contained"
                          className={classes.confirmBtn}
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => handleSend("CONFIRMO")}
                          disabled={loading}
                        >
                          Confirmar y Ejecutar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          className={classes.cancelBtn}
                          startIcon={<HighlightOffIcon />}
                          onClick={() => handleSend("CANCELAR")}
                          disabled={loading}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Indicador de Análisis */}
          {loading && (
            <Fade in={loading}>
              <div className={classes.typingIndicator}>
                <CircularProgress size={13} thickness={5} color="primary" />
                <Typography variant="caption" style={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                  Jarvis está analizando los datos del CRM...
                </Typography>
              </div>
            </Fade>
          )}

          <div ref={chatEndRef} />
        </DialogContent>

        {/* ── Barra de Entrada Unificada y Centrada ────── */}
        <div className={classes.inputArea}>
          <div className={classes.inputCapsule}>
            <Tooltip title={isListening ? "Detener dictado" : "Dictar por voz"}>
              <IconButton
                size="small"
                color="primary"
                onClick={toggleListening}
                className={isListening ? classes.micActive : ""}
                style={{ padding: 6 }}
              >
                {isListening ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <TextField
              fullWidth
              placeholder="Escribe o dicta una instrucción a Jarvis..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading}
              multiline
              maxRows={4}
              className={classes.textFieldSeamless}
              InputProps={{
                disableUnderline: true,
              }}
            />

            <Tooltip title="Enviar mensaje">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => handleSend()}
                  disabled={loading || !input.trim()}
                  size="small"
                  style={{ padding: 6 }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </Dialog>

      {/* ── Menú Desplegable 'Comandos y Memoria Jarvis' ────── */}
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            maxHeight: 460,
            width: 320,
            borderRadius: 14,
            zIndex: 1600,
            boxShadow: theme.palette.type === "dark" 
              ? "0 12px 36px rgba(0,0,0,0.7)" 
              : "0 12px 36px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box px={2} py={1} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Acciones y Memoria Jarvis
          </Typography>
          <Tooltip title="Agregar nueva tarea">
            <IconButton 
              size="small" 
              onClick={() => {
                handleCloseMenu();
                setCreateModalOpen(true);
              }}
            >
              <AddIcon fontSize="small" style={{ color: "#10B981" }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />

        {/* Sección: Mis Tareas Frecuentes */}
        <Box px={2} pt={1.2} pb={0.5}>
          <Typography 
            variant="caption" 
            color="textSecondary" 
            style={{ fontWeight: 700, textTransform: "uppercase", fontSize: "0.68rem" }}
          >
            Mis Tareas Frecuentes ({customActions.length})
          </Typography>
        </Box>

        {customActions.length === 0 ? (
          <Box px={2} py={1}>
            <Typography variant="caption" color="textSecondary">
              No tienes tareas guardadas. Crea una con '+ Nueva Tarea'.
            </Typography>
          </Box>
        ) : (
          customActions.map((action) => (
            <MenuItem
              key={action.id}
              onClick={() => {
                handleCloseMenu();
                handleSend(action.prompt);
              }}
              style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 16px" }}
            >
              <Box display="flex" alignItems="center" gap={1} style={{ overflow: "hidden" }}>
                <StarIcon style={{ fontSize: 18, color: "#F59E0B", flexShrink: 0 }} />
                <Typography variant="body2" noWrap style={{ fontWeight: 600 }}>
                  {action.label}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCustomAction(action.id);
                }}
                style={{ padding: 4 }}
                title="Eliminar tarea"
              >
                <DeleteOutlineIcon fontSize="small" color="secondary" />
              </IconButton>
            </MenuItem>
          ))
        )}

        <Divider style={{ margin: "6px 0" }} />

        {/* Sección: Consultas del Sistema */}
        <Box px={2} pt={0.5} pb={0.5}>
          <Typography 
            variant="caption" 
            color="textSecondary" 
            style={{ fontWeight: 700, textTransform: "uppercase", fontSize: "0.68rem" }}
          >
            Consultas del Sistema
          </Typography>
        </Box>

        {QUICK_ACTIONS.map((action, idx) => (
          <MenuItem
            key={`menu-sys-${idx}`}
            onClick={() => {
              handleCloseMenu();
              handleSend(action.prompt);
            }}
            style={{ padding: "8px 16px" }}
          >
            <ListItemIcon style={{ minWidth: 32 }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText 
              primary={action.label} 
              primaryTypographyProps={{ variant: "body2", style: { fontWeight: 500 } }} 
            />
          </MenuItem>
        ))}

        <Divider />
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            setCreateModalOpen(true);
          }}
          style={{ color: "#10B981", fontWeight: 600, padding: "10px 16px" }}
        >
          <ListItemIcon style={{ minWidth: 32, color: "#10B981" }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Crear Nueva Tarea Rápida..." />
        </MenuItem>
      </Menu>

      {/* ── Modal 'Nueva Tarea Rápida' ────── */}
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="xs"
        fullWidth
        style={{ zIndex: 1700 }}
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ padding: "16px 20px 8px 20px" }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Nueva Tarea Frecuente
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Almacena consultas o instrucciones recurrentes para ejecutarlas con 1 clic en Jarvis.
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: "8px 20px" }}>
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            label="Nombre del botón o tarea"
            placeholder="Ej: Leads Etapa Lima"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            autoFocus
          />
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            label="Instrucción detallada para Jarvis"
            placeholder="Ej: Dame un reporte detallado de los prospectos y leads en la etapa Lima con sus nombres y teléfonos."
            value={newTaskPrompt}
            onChange={(e) => setNewTaskPrompt(e.target.value)}
            multiline
            rows={3}
            helperText="Jarvis ejecutará este comando exactamente al hacer clic en el botón."
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px 16px 20px" }}>
          <Button 
            onClick={() => setCreateModalOpen(false)} 
            color="default" 
            style={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveCustomAction}
            variant="contained"
            color="primary"
            disabled={!newTaskTitle.trim() || !newTaskPrompt.trim()}
            style={{ textTransform: "none", fontWeight: 700, borderRadius: 8 }}
          >
            Guardar Tarea
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JarvisChatModal;
